/**
 * EVENTSFLO-CLIENT-PORTAL-PHASE-1.5-QUOTES-INVOICES-V1
 * Client Quotes — read-only list + detail modal with PDF download.
 *
 * Strictly scoped to the authenticated client via the JWT.
 * Draft quotes are never shown.
 */
import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Separator } from "../../components/ui/separator";
import { ScrollArea } from "../../components/ui/scroll-area";
import { toast } from "sonner";
import {
  FileText,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Filter,
  Download,
  Eye,
  Calendar,
  ThumbsUp,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const VISIBLE_QUOTE_STATUSES = [
  "sent",
  "approved",
  "accepted",
  "rejected",
  "expired",
];

const statusColor = (status) => {
  switch ((status || "").toLowerCase()) {
    case "accepted":
    case "approved":
      return "bg-green-500/10 text-green-600 border-green-500/20";
    case "sent":
      return "bg-blue-500/10 text-blue-600 border-blue-500/20";
    case "rejected":
      return "bg-red-500/10 text-red-600 border-red-500/20";
    case "expired":
      return "bg-yellow-500/10 text-yellow-600 border-yellow-500/20";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const formatMoney = (amount, currencySymbol) => {
  const num = Number(amount || 0);
  const sym = currencySymbol || "€";
  return `${sym}${num.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

export default function ClientQuotesPage() {
  const { getToken } = useOutletContext();
  // EVENTSFLO-PORTAL-I18N-PHASE-2B-CLIENT-PORTAL-COVERAGE-V1
  const { t } = useTranslation(["clientPortal", "common"]);

  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [meta, setMeta] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0,
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchQuotes = useCallback(
    async (page = 1, status = "all") => {
      const token = getToken();
      if (!token) return;

      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          page_size: "20",
        });
        if (status && status !== "all") {
          params.append("status", status);
        }

        const response = await fetch(
          `${API_URL}/api/client/quotes?${params}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error("Failed to fetch quotes");
        const data = await response.json();
        setQuotes(data.data || []);
        setMeta(
          data.meta || { page: 1, limit: 20, total: 0, total_pages: 0 }
        );
      } catch (err) {
        console.error("Quotes fetch error:", err);
        toast.error(t("clientPortal:quotes.fetchFailed"));
      } finally {
        setLoading(false);
      }
    },
    [getToken, t]
  );

  useEffect(() => {
    fetchQuotes(1, statusFilter);
  }, [fetchQuotes, statusFilter]);

  const openDetail = async (quoteId) => {
    const token = getToken();
    if (!token) return;
    setDetail(null);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/client/quotes/${quoteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("Failed to fetch quote");
      const data = await response.json();
      setDetail(data);
    } catch (err) {
      console.error("Quote detail error:", err);
      toast.error(t("clientPortal:quotes.detailFailed"));
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const viewPdf = async (quoteId) => {
    const token = getToken();
    if (!token) return;
    try {
      const response = await fetch(
        `${API_URL}/api/client/quotes/${quoteId}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("Failed to load PDF");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        toast.error(t("clientPortal:quotes.popupBlocked"));
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error("PDF view error:", err);
      toast.error(t("clientPortal:quotes.pdfViewFailed"));
    }
  };

  const downloadPdf = async (quoteId, quoteNumber) => {
    const token = getToken();
    if (!token) return;
    try {
      const response = await fetch(
        `${API_URL}/api/client/quotes/${quoteId}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("Failed to download PDF");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Quote_${quoteNumber || quoteId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("clientPortal:quotes.pdfDownloaded"));
    } catch (err) {
      console.error("PDF download error:", err);
      toast.error(t("clientPortal:quotes.pdfDownloadFailed"));
    }
  };

  // EVENTSFLO-QUOTE-LIFECYCLE-CLIENT-ACCEPTANCE-ADMIN-SEND-V1
  const acceptQuote = async (quote) => {
    const token = getToken();
    if (!token) return;
    const ok = window.confirm(
      t("clientPortal:quotes.confirmAccept", {
        number: quote.quote_number,
        total: formatMoney(quote.total, quote.currency_symbol),
      })
    );
    if (!ok) return;
    try {
      const res = await fetch(
        `${API_URL}/api/client/quotes/${quote.id}/accept`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || t("clientPortal:quotes.acceptFailed"));
      }
      toast.success(t("clientPortal:quotes.acceptedToast", { number: quote.quote_number }));
      // Refresh list (current page + filter).
      fetchQuotes(meta.page || 1, statusFilter);
      // If detail modal open, refresh it too.
      if (detail && detail.id === quote.id) {
        setDetail({ ...detail, status: "accepted" });
      }
    } catch (err) {
      console.error("Accept quote error:", err);
      toast.error(err.message || t("clientPortal:quotes.acceptFailed"));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold" data-testid="quotes-title">
            {t("clientPortal:quotes.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("clientPortal:quotes.totalLabel", { count: meta.total })}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              className="w-[160px]"
              data-testid="quote-status-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("clientPortal:quotes.filterAllStatuses")}</SelectItem>
              {VISIBLE_QUOTE_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`clientPortal:quotes.status.${s}`, {
                    defaultValue: s.charAt(0).toUpperCase() + s.slice(1),
                  })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : quotes.length === 0 ? (
        <Card>
          <CardContent
            className="py-12 text-center"
            data-testid="quotes-empty-state"
          >
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t("clientPortal:quotes.empty")}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-2">
            {quotes.map((q) => (
              <Card
                key={q.id}
                className="hover:bg-accent/30 transition-colors"
                data-testid={`quote-row-${q.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 bg-primary/10 text-primary rounded-lg flex items-center justify-center">
                      <FileText className="h-5 w-5" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-semibold text-sm">
                          {q.quote_number}
                        </h3>
                        <Badge
                          variant="outline"
                          className={`text-[10px] capitalize ${statusColor(q.status)}`}
                        >
                          {t(`clientPortal:quotes.status.${(q.status || "").toLowerCase()}`, {
                            defaultValue: q.status,
                          })}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {q.project_name || "—"}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {t("clientPortal:quotes.issuedPrefix", { date: formatDate(q.issue_date) })}
                        </span>
                        <span>{t("clientPortal:quotes.validUntilPrefix", { date: formatDate(q.valid_until) })}</span>
                      </div>
                      {/* EVENTSFLO-QUOTE-ACCEPTANCE-AUDIT-NOTES-V1 */}
                      {(q.status || "").toLowerCase() === "accepted" && q.accepted_at && (
                        <p
                          className="text-xs text-green-600 mt-1 flex items-center gap-1"
                          data-testid={`portal-quote-accepted-meta-${q.id}`}
                        >
                          <ThumbsUp className="h-3 w-3" />
                          {t("clientPortal:quotes.acceptedAt", { when: new Date(q.accepted_at).toLocaleString() })}
                          {q.acceptance_source === "admin_manual" ? t("clientPortal:quotes.recordedByUs") : ""}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <div
                        className="text-base font-semibold"
                        data-testid={`quote-total-${q.id}`}
                      >
                        {formatMoney(q.total, q.currency_symbol)}
                      </div>
                      <div className="flex flex-wrap gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetail(q.id)}
                          data-testid={`quote-view-${q.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          {t("clientPortal:quotes.actions.detail")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewPdf(q.id)}
                          data-testid={`quote-view-pdf-${q.id}`}
                          title={t("clientPortal:quotes.actions.viewPdf")}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          {t("clientPortal:quotes.actions.viewPdf")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadPdf(q.id, q.quote_number)}
                          data-testid={`quote-download-${q.id}`}
                          title={t("clientPortal:quotes.actions.viewPdf")}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          {t("clientPortal:quotes.actions.download")}
                        </Button>
                        {["sent", "approved"].includes((q.status || "").toLowerCase()) && (
                          <Button
                            size="sm"
                            onClick={() => acceptQuote(q)}
                            data-testid={`quote-accept-${q.id}`}
                            title={t("clientPortal:acceptThisQuote")}
                          >
                            <ThumbsUp className="h-4 w-4 mr-1" />
                            {t("clientPortal:quotes.actions.accept")}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {meta.total_pages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                {t("clientPortal:quotes.pagination.pageOf", { page: meta.page, total: meta.total_pages })}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page <= 1}
                  onClick={() => fetchQuotes(meta.page - 1, statusFilter)}
                  data-testid="quotes-prev-page-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {t("clientPortal:quotes.pagination.previous")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page >= meta.total_pages}
                  onClick={() => fetchQuotes(meta.page + 1, statusFilter)}
                  data-testid="quotes-next-page-btn"
                >
                  {t("clientPortal:quotes.pagination.next")}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent
          className="max-w-2xl max-h-[85vh]"
          data-testid="quote-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              {detail?.quote_number || t("clientPortal:quotes.detail.fallbackTitle")}
              {detail?.status && (
                <Badge
                  variant="outline"
                  className={`text-[10px] capitalize ml-2 ${statusColor(detail.status)}`}
                >
                  {t(`clientPortal:quotes.status.${(detail.status || "").toLowerCase()}`, {
                    defaultValue: detail.status,
                  })}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : detail ? (
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">{t("clientPortal:quotes.detail.project")}</p>
                    <p className="font-medium">{detail.project_name || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("clientPortal:quotes.detail.issueDate")}</p>
                    <p className="font-medium">
                      {formatDate(detail.issue_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("clientPortal:quotes.detail.validUntil")}</p>
                    <p className="font-medium">
                      {formatDate(detail.valid_until)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("clientPortal:quotes.detail.currency")}</p>
                    <p className="font-medium">
                      {detail.currency_code || "EUR"}
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="text-sm font-semibold mb-2">{t("clientPortal:quotes.detail.lineItems")}</h4>
                  <div className="space-y-1 text-xs">
                    {(detail.lines || []).map((ln, i) => (
                      <div
                        key={i}
                        className="grid grid-cols-12 gap-2 py-1.5 border-b border-border/40"
                        data-testid={`quote-line-${i}`}
                      >
                        <div className="col-span-7">
                          <p className="font-medium">{ln.description}</p>
                          {ln.shift_name && (
                            <p className="text-[10px] text-muted-foreground">
                              {ln.shift_name}
                              {ln.shift_date ? ` • ${ln.shift_date}` : ""}
                            </p>
                          )}
                        </div>
                        <div className="col-span-2 text-right">
                          {ln.quantity}
                        </div>
                        <div className="col-span-3 text-right">
                          {formatMoney(ln.line_total, detail.currency_symbol)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("clientPortal:quotes.detail.subtotal")}</span>
                    <span>
                      {formatMoney(detail.subtotal, detail.currency_symbol)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("clientPortal:quotes.detail.vat")}</span>
                    <span>
                      {formatMoney(detail.tax_amount, detail.currency_symbol)}
                    </span>
                  </div>
                  <div className="flex justify-between font-semibold text-base pt-1">
                    <span>{t("clientPortal:quotes.detail.total")}</span>
                    <span data-testid="quote-detail-total">
                      {formatMoney(detail.total, detail.currency_symbol)}
                    </span>
                  </div>
                </div>

                {detail.notes && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        {t("clientPortal:quotes.detail.notes")}
                      </p>
                      <p className="text-sm whitespace-pre-wrap">
                        {detail.notes}
                      </p>
                    </div>
                  </>
                )}

                <div className="pt-3 flex justify-end gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    onClick={() => viewPdf(detail.id)}
                    data-testid="quote-detail-view-pdf-btn"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    {t("clientPortal:quotes.detail.viewPdf")}
                  </Button>
                  <Button
                    onClick={() => downloadPdf(detail.id, detail.quote_number)}
                    data-testid="quote-detail-download-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {t("clientPortal:quotes.detail.downloadPdf")}
                  </Button>
                  {["sent", "approved"].includes((detail.status || "").toLowerCase()) && (
                    <Button
                      onClick={() => acceptQuote(detail)}
                      data-testid="quote-detail-accept-btn"
                    >
                      <ThumbsUp className="h-4 w-4 mr-2" />
                      {t("clientPortal:quotes.detail.acceptQuote")}
                    </Button>
                  )}
                </div>
              </div>
            </ScrollArea>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

